import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Save, Trash2 } from "lucide-react";
import { brokersApi } from "@/api/brokers";
import type { BrokerSource, UpsertBrokerPayload } from "@/api/brokers";
import { brokerKeys } from "@/hooks/brokers/brokerKeys";
import { getBrokerDisplayName } from "@/lib/brokerDisplay";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { RoleFilteredUserPicker } from "@/components/DealFlow/common/RoleFilteredUserPicker";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { US_STATES } from "@/lib/usStates";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";

const SOURCES: BrokerSource[] = ["IBBA", "MaSource", "LinkedIn", "Referral", "BizBuySell", "Other"];

const LICENSE_TYPE_OPTIONS = [
  "Real Estate Broker License",
  "Real Estate Salesperson License",
  "Associate Broker License",
  "Business Broker Permit",
  "Business Broker Registration",
  "Securities Broker-Dealer Registration",
  "Registered Representative / FINRA License",
  "M&A Broker / M&A Intermediary",
  "No License Required in My State",
  "Other",
];

export function BrokerEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editing = !!id;

  const [form, setForm] = useState<UpsertBrokerPayload>({
    companyName: "",
    dba: "",
    name: "",
    title: "",
    email: "",
    phone: "",
    source: "Other",
    sourceDetail: "",
    eoInsuranceProvider: "",
    eoPolicyNumber: "",
    eoExpirationDate: null,
    eoCoverageAmount: null,
    licenseNumber: "",
    licenseState: "",
    licenseExpirationDate: null,
    linkedInProfile: "",
    yearsOfExperience: null,
    numberOfTransactionsClosed: null,
    primaryAreasOfExpertise: "",
    industriesServed: "",
    geographicMarketsServed: "",
    professionalCertificationsLicensesDesignations: "",
    phoenixCapitalInterest: "",
    businessBrokerageLicenseTypes: [],
    licensingDetails: [],
    website: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    assignedC1LiaisonUserId: "",
    assignedC0ManagerUserId: "",
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);

  const existing = useQuery({
    queryKey: brokerKeys.detail(id ?? ""),
    queryFn: () => brokersApi.get(id!),
    enabled: editing,
  });

  // Default breadcrumb is URL-derived, which renders the raw broker GUID. Show
  // the broker's name (linking back to the detail page) instead.
  useBreadcrumbs(
    editing
      ? existing.data
        ? [
            { label: "Brokers", href: "/brokers" },
            { label: getBrokerDisplayName(existing.data), href: `/brokers/${id}` },
            { label: "Edit" },
          ]
        : null
      : [
          { label: "Brokers", href: "/brokers" },
          { label: "New broker" },
        ],
  );

  useEffect(() => {
    if (existing.data) {
      const b = existing.data;
      setForm({
        companyName: b.companyName,
        dba: b.dba ?? "",
        name: b.name ?? "",
        title: b.title ?? "",
        email: b.email ?? "",
        phone: b.phone ?? "",
        source: b.source,
        sourceDetail: b.sourceDetail ?? "",
        eoInsuranceProvider: b.eoInsuranceProvider ?? "",
        eoPolicyNumber: b.eoPolicyNumber ?? "",
        eoExpirationDate: b.eoExpirationDate ?? null,
        eoCoverageAmount: b.eoCoverageAmount ?? null,
        licenseNumber: b.licenseNumber ?? "",
        licenseState: b.licenseState ?? "",
        licenseExpirationDate: b.licenseExpirationDate ?? null,
        linkedInProfile: b.linkedInProfile ?? "",
        yearsOfExperience: b.yearsOfExperience ?? null,
        numberOfTransactionsClosed: b.numberOfTransactionsClosed ?? null,
        primaryAreasOfExpertise: b.primaryAreasOfExpertise ?? "",
        industriesServed: b.industriesServed ?? "",
        geographicMarketsServed: b.geographicMarketsServed ?? "",
        professionalCertificationsLicensesDesignations: b.professionalCertificationsLicensesDesignations ?? "",
        phoenixCapitalInterest: b.phoenixCapitalInterest ?? "",
        businessBrokerageLicenseTypes: b.businessBrokerageLicenseTypes ?? [],
        licensingDetails: b.licensingDetails ?? [],
        website: b.website ?? "",
        addressLine1: b.addressLine1 ?? "",
        addressLine2: b.addressLine2 ?? "",
        city: b.city ?? "",
        state: b.state ?? "",
        postalCode: b.postalCode ?? "",
        assignedC1LiaisonUserId: b.assignedC1LiaisonUserId ?? "",
        assignedC0ManagerUserId: b.assignedC0ManagerUserId ?? "",
        notes: b.notes ?? "",
      });
    }
  }, [existing.data]);

  const mutation = useMutation({
    mutationFn: (body: UpsertBrokerPayload) =>
      editing ? brokersApi.update(id!, body) : brokersApi.create(body),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: brokerKeys.all });
      navigate(`/brokers/${res.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const set = <K extends keyof UpsertBrokerPayload>(key: K, val: UpsertBrokerPayload[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  // The broker's own email/phone are optional, but if present must be valid
  // (matches the server-side format validation). Edited on both add and edit.
  // WS3: this single contact Email is ALSO the portal-login source — a change
  // is synced to the linked PortalUser.Email server-side. The separate
  // "Portal login email" input (dead Broker.PortalLoginEmail column) is gone.
  const brokerEmailError = emailFieldError(form.email, { required: false });
  const brokerPhoneError = phoneFieldError(form.phone, { required: false });

  return (
    <>
      <PageHeader
        eyebrow="Brokers"
        title={editing ? "Edit broker" : "New broker"}
        actions={
          <>
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
            <Button
              size="sm"
              loading={mutation.isPending}
              disabled={!form.companyName || !!brokerEmailError || !!brokerPhoneError}
              onClick={() => {
                setError(null);
                if (brokerEmailError || brokerPhoneError) {
                  setError(brokerEmailError ?? brokerPhoneError ?? null);
                  return;
                }
                mutation.mutate(form);
              }}
            >
              <Save size={14} strokeWidth={1.5} aria-hidden /> Save broker
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Company Name" required helperText="If you're an independent contractor or employed by a brokerage and do not have your own company, leave this blank."><Input value={form.companyName} onChange={(e) => set("companyName", e.target.value)} /></FormField>
        <FormField label="DBA / trade name"><Input value={form.dba ?? ""} onChange={(e) => set("dba", e.target.value)} /></FormField>
        <FormField label="Website"><Input value={form.website ?? ""} onChange={(e) => set("website", e.target.value)} /></FormField>
        <FormField label="Source">
          <Select value={form.source} onChange={(e) => set("source", e.target.value as BrokerSource)}>
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormField>
        <FormField label="Source detail"><Input value={form.sourceDetail ?? ""} onChange={(e) => set("sourceDetail", e.target.value)} /></FormField>
      </div>

      {/* The broker's own contact details — edited directly on add and edit.
          Separate from the Contacts card on the detail page. */}
      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">Contact</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Name"><Input value={form.name ?? ""} onChange={(e) => set("name", e.target.value)} /></FormField>
        <FormField label="Title"><Input value={form.title ?? ""} onChange={(e) => set("title", e.target.value)} /></FormField>
        <FormField label="Email" error={brokerEmailError}>
          <Input
            type="email"
            value={form.email ?? ""}
            onChange={(e) => set("email", e.target.value)}
            invalid={!!brokerEmailError}
          />
        </FormField>
        <FormField label="Phone" error={brokerPhoneError}>
          <Input
            type="tel"
            value={form.phone ?? ""}
            onChange={(e) => set("phone", e.target.value)}
            invalid={!!brokerPhoneError}
          />
        </FormField>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">Address</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Address 1" className="md:col-span-2"><Input value={form.addressLine1 ?? ""} onChange={(e) => set("addressLine1", e.target.value)} /></FormField>
        <FormField label="Address 2" className="md:col-span-2"><Input value={form.addressLine2 ?? ""} onChange={(e) => set("addressLine2", e.target.value)} /></FormField>
        <FormField label="City"><Input value={form.city ?? ""} onChange={(e) => set("city", e.target.value)} /></FormField>
        <FormField label="State">
          <Select value={form.state ?? ""} onChange={(e) => set("state", e.target.value || null)}>
            <option value="">—</option>
            {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
          </Select>
        </FormField>
        <FormField label="ZIP"><Input value={form.postalCode ?? ""} onChange={(e) => set("postalCode", e.target.value)} /></FormField>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">E&amp;O Insurance</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Provider"><Input value={form.eoInsuranceProvider ?? ""} onChange={(e) => set("eoInsuranceProvider", e.target.value)} /></FormField>
        <FormField label="Policy #"><Input value={form.eoPolicyNumber ?? ""} onChange={(e) => set("eoPolicyNumber", e.target.value)} /></FormField>
        <FormField label="Expiration"><Input type="date" value={form.eoExpirationDate ?? ""} onChange={(e) => set("eoExpirationDate", e.target.value || null)} /></FormField>
        <FormField label="Coverage amount (USD)"><Input type="number" value={form.eoCoverageAmount ?? ""} onChange={(e) => set("eoCoverageAmount", e.target.value ? Number(e.target.value) : null)} /></FormField>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">Experience &amp; markets</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="LinkedIn Profile"><Input value={form.linkedInProfile ?? ""} onChange={(e) => set("linkedInProfile", e.target.value)} /></FormField>
        <FormField label="Years of Experience"><Input type="number" value={form.yearsOfExperience ?? ""} onChange={(e) => set("yearsOfExperience", e.target.value ? Number(e.target.value) : null)} /></FormField>
        <FormField label="Number of Transactions Closed"><Input type="number" value={form.numberOfTransactionsClosed ?? ""} onChange={(e) => set("numberOfTransactionsClosed", e.target.value ? Number(e.target.value) : null)} /></FormField>
        <FormField label="Primary Areas of Expertise" className="md:col-span-2"><Textarea rows={3} value={form.primaryAreasOfExpertise ?? ""} onChange={(e) => set("primaryAreasOfExpertise", e.target.value)} /></FormField>
        <FormField label="Industries Served" className="md:col-span-2"><Textarea rows={3} value={form.industriesServed ?? ""} onChange={(e) => set("industriesServed", e.target.value)} /></FormField>
        <FormField label="States and Geographic Markets Served" className="md:col-span-2"><Textarea rows={3} value={form.geographicMarketsServed ?? ""} onChange={(e) => set("geographicMarketsServed", e.target.value)} /></FormField>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">Licensing</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="License #"><Input value={form.licenseNumber ?? ""} onChange={(e) => set("licenseNumber", e.target.value)} /></FormField>
        <FormField label="License state">
          <Select value={form.licenseState ?? ""} onChange={(e) => set("licenseState", e.target.value || null)}>
            <option value="">—</option>
            {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
          </Select>
        </FormField>
        <FormField label="Expiration"><Input type="date" value={form.licenseExpirationDate ?? ""} onChange={(e) => set("licenseExpirationDate", e.target.value || null)} /></FormField>
        <FormField label="Professional Certifications, Licenses, and Designations" className="md:col-span-3"><Textarea rows={3} value={form.professionalCertificationsLicensesDesignations ?? ""} onChange={(e) => set("professionalCertificationsLicensesDesignations", e.target.value)} /></FormField>
        <FormField label="Business Brokerage License Type" className="md:col-span-3">
          <div className="flex flex-wrap gap-space-2">
            {LICENSE_TYPE_OPTIONS.map((option) => {
              const selected = (form.businessBrokerageLicenseTypes ?? []).includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => set("businessBrokerageLicenseTypes", selected
                    ? (form.businessBrokerageLicenseTypes ?? []).filter((x) => x !== option)
                    : [...(form.businessBrokerageLicenseTypes ?? []), option])}
                  className={`px-space-2 py-[3px] text-caption rounded-pill border ${selected ? "bg-gold/20 border-gold text-onyx" : "bg-white-pure border-border-light text-gray-mid"}`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </FormField>
        <div className="md:col-span-3 flex flex-col gap-space-2">
          <div className="flex items-center justify-between">
            <span className="text-small font-medium text-onyx">Licensing Details</span>
            <Button type="button" variant="secondary" size="sm" onClick={() => set("licensingDetails", [...(form.licensingDetails ?? []), { licenseTypeIssuingOrganization: "", licenseNumber: "", expirationDate: null, state: "" }])}>
              <Plus size={14} aria-hidden /> Add license row
            </Button>
          </div>
          {(form.licensingDetails ?? []).map((row, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-space-2 rounded border border-border-light p-space-2">
              <FormField label="License Type & Issuing Organization" className="md:col-span-2"><Input value={row.licenseTypeIssuingOrganization ?? ""} onChange={(e) => set("licensingDetails", (form.licensingDetails ?? []).map((r, i) => i === index ? { ...r, licenseTypeIssuingOrganization: e.target.value } : r))} /></FormField>
              <FormField label="License Number"><Input value={row.licenseNumber ?? ""} onChange={(e) => set("licensingDetails", (form.licensingDetails ?? []).map((r, i) => i === index ? { ...r, licenseNumber: e.target.value } : r))} /></FormField>
              <FormField label="Expiration Date"><Input type="date" value={row.expirationDate ?? ""} onChange={(e) => set("licensingDetails", (form.licensingDetails ?? []).map((r, i) => i === index ? { ...r, expirationDate: e.target.value || null } : r))} /></FormField>
              <div className="flex items-end gap-space-2">
                <FormField label="State" className="flex-1"><Input value={row.state ?? ""} onChange={(e) => set("licensingDetails", (form.licensingDetails ?? []).map((r, i) => i === index ? { ...r, state: e.target.value } : r))} /></FormField>
                <Button type="button" variant="destructive" size="sm" aria-label="Remove license row" onClick={() => set("licensingDetails", (form.licensingDetails ?? []).filter((_, i) => i !== index))}>
                  <Trash2 size={14} aria-hidden />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">Biography &amp; Phoenix Interest</h3>
      <div className="grid grid-cols-1 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Why Are You Interested in Phoenix Capital?"><Textarea rows={4} value={form.phoenixCapitalInterest ?? ""} onChange={(e) => set("phoenixCapitalInterest", e.target.value)} /></FormField>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">Assignments &amp; notes</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="C1 Liaison">
          <RoleFilteredUserPicker
            role="C1,C0,Admin"
            value={form.assignedC1LiaisonUserId ?? null}
            onChange={(userId) => set("assignedC1LiaisonUserId", userId)}
            placeholder="Unassigned"
            aria-label="C1 Liaison"
          />
        </FormField>
        <FormField label="C0 Manager">
          <RoleFilteredUserPicker
            role="C0,Admin"
            value={form.assignedC0ManagerUserId ?? null}
            onChange={(userId) => set("assignedC0ManagerUserId", userId)}
            placeholder="Unassigned"
            aria-label="C0 Manager"
          />
        </FormField>
        <FormField label="Notes" className="md:col-span-2"><Textarea rows={4} value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} /></FormField>
      </div>

      {error && <p className="text-caption text-status-error mt-space-3" role="alert">{error}</p>}
    </>
  );
}
